import { useCallback, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useMutation } from 'react-query';

import {
  DOCUMENT_TYPES,
  NOTIFICATION_TYPES,
} from '@savgroup-front-common/constants';
import { ADDITIONAL_INFORMATION_TYPES } from '@savgroup-front-common/types';

import { CommonAttachmentService } from '../../../api/Attachment';
import { CommonCatalogService } from '../../../api/Catalog';
import { safeFormattedIntlString } from '../../../formatters';
import {
  base64FromArrayBuffer,
  buildNotification,
  delay,
} from '../../../helpers';
import { useToasts } from '../../../molecules/NotificationsProvider';
import { useAdditionalInformationContext } from '../AdditionalInformation.context';
import { buildInputName } from '../AdditionalInformationInput.helpers';
import { AdditionalInformation } from '../hooks/useAdditionalInformation';

import { verifyThatFileNeedToBeUploadedToAttachment } from './AdditionalInformationSerialNumber.helpers';
import messages from './messages';

export const useAdditionalInformationSerialNumber = ({
  name,
  neededInformationItem,
}: {
  name: string;
  neededInformationItem: AdditionalInformation;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const { pushError, pushNotification } = useToasts();
  const { claimIds } = neededInformationItem;
  const {
    neededInformation: neededInformationItems,
    prefixFormName,
    onAdditionalInformationSave,
    onNeedToUploadFileAttachements,
  } = useAdditionalInformationContext();

  const { setValue } = useFormContext();

  const { mutateAsync: onNewFileUpload, isLoading } = useMutation<
    void,
    unknown,
    {
      file: File;
    }
  >(['onNewFileUpload'], async ({ file }: { file: File }) => {
    try {
      const base64 = await base64FromArrayBuffer(file);

      const response = await CommonCatalogService.recognizeSerialNumberCommand({
        imageBase64: base64,
      });

      if (response.failure) {
        pushNotification(
          buildNotification({
            notificationType: NOTIFICATION_TYPES.INFO,
            message: safeFormattedIntlString(messages.unrecognizedSerialNumber),
          }),
        );

        return;
      }

      if (response.value) {
        setValue(name, response.value, {
          shouldValidate: true,
          shouldDirty: true,
        });
      } else {
        pushNotification(
          buildNotification({
            notificationType: NOTIFICATION_TYPES.INFO,
            message: safeFormattedIntlString(messages.unrecognizedSerialNumber),
          }),
        );

        return;
      }

      const isNeedToUploadToAttachments =
        verifyThatFileNeedToBeUploadedToAttachment({
          neededInformationItems,
          name,
          prefixFormName,
          claimIds,
        });

      if (isNeedToUploadToAttachments) {
        if (onNeedToUploadFileAttachements) {
          onNeedToUploadFileAttachements({ files: [file] });
        }
      } else {
        neededInformationItems.map((neededInformationItem) => {
          const targetName = buildInputName({
            prefixFormName,
            name: neededInformationItem.internalId,
          });

          if (targetName === name) {
            return;
          }

          claimIds?.map((claimId: string) => {
            if (
              claimId &&
              neededInformationItem.documentType ===
                DOCUMENT_TYPES.NAME_PLATE &&
              neededInformationItem.type ===
                ADDITIONAL_INFORMATION_TYPES.FILE &&
              neededInformationItem.claimIds?.includes(claimId) &&
              neededInformationItem.claimIds.length > 0
            ) {
              setValue(
                targetName,
                {
                  value: file,
                },
                {
                  shouldValidate: true,
                  shouldDirty: true,
                },
              );
            }
          });
        });
      }

      if (onAdditionalInformationSave) {
        await onAdditionalInformationSave();
      }
    } catch (error) {
      pushError(
        new Error(safeFormattedIntlString(messages.errorProcessingFile)),
      );
    }
  });

  const handleUploadFinishClick = useCallback(
    async ({ sessionId }: { sessionId: string }) => {
      try {
        await delay(500);

        const response =
          await CommonAttachmentService.getFilesDownloadLinksBySessionIdQuery({
            sessionId,
          });

        if (response.failure) {
          pushError(
            new Error(safeFormattedIntlString(messages.errorProcessingFile)),
          );

          setIsOpen(false);

          return undefined;
        }

        if (!response.value?.links?.length) {
          return undefined;
        }

        const firstLink = response.value.links[0];
        const blob = await (await fetch(firstLink)).blob();
        const file = new File([blob], name, {
          type: blob.type,
        });

        if ('failure' in file) {
          pushError(
            new Error(safeFormattedIntlString(messages.errorProcessingFile)),
          );
          setIsOpen(false);

          return undefined;
        }

        await onNewFileUpload({ file });
      } catch (error) {
        pushError(
          new Error(safeFormattedIntlString(messages.errorProcessingFile)),
        );
      } finally {
        setIsOpen(false);
      }
    },
    [name, onNewFileUpload, pushError],
  );
  const handleQrCodeButtonClick = useCallback(async () => {
    setIsOpen(true);
  }, []);

  return {
    isOpen,
    onNewFileUpload,
    handleUploadFinishClick,
    isLoading,
    handleQrCodeButtonClick,
  };
};
